import * as Sentry from "@sentry/react";
import {
  localStoragePersister,
  NEVER_PERSISTED_ROUTES,
} from "./services/queryPersistence";
import { defaultShouldDehydrateQuery } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { Redirector } from "react-router-redirect";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import {
  growthbook,
  growthbookIdentityStore,
  initGrowthbook,
} from "./tools/growthbook";
import "./App.css";
import AppRoutes from "./AppRoutes";
import { AppToaster } from "./components/app/AppToast";
import { isDesktopShell } from "./common/hooks/useIsDesktopView";
import { env } from "./config/env";
import ErrorFallback from "./components/error/ErrorFallback";
import Loader from "./components/Loader";
import { AudioProvider } from "./domains/app/contexts/audio.context";
import { LangSync } from "./domains/app/components/LangSync";

import useIsOffline from "./hooks/use-is-offline";
import { useRequestReview } from "./hooks/use-request-review";
import { useStorageCleanup } from "./hooks/use-storage-cleanup";
import { useAppForegroundedTracking } from "./hooks/use-app-foregrounded";
import {
  gaInitializeTagManager,
  gaStartEngagementHeartbeat,
} from "./tools/analytics/base.analytics";
import { gaSetCampaignDetails } from "./tools/analytics/domains/onboarding.analytics";
import { iosGetSafeArea, iosSetBackgroundColor, isIOSApp } from "./tools/ios";
import { dismissNativeKeyboardOnDrag } from "./tools/native-keyboard";
import { AppLayout } from "./components/app/layout/AppLayout";
import { SettingsSheet } from "./components/settings/sheet/SettingsSheet";
import { ProfessionalRecordHost } from "./components/room/ProfessionalRecordHost";
import { RoomWelcomeHost } from "./components/room/RoomWelcomeHost";
import { AnnotationSheet } from "./components/gpt/annotation/AnnotationSheet";
import { DefaultThemeProvider } from "./domains/app/contexts/theme.context";
import { queryClient, QUERY_CACHE_VERSION } from "./queryClient";
import {
  useMixpanel,
  useMixpanelJuisciGptOnboarded,
  useMixpanelUser,
} from "./hooks/use-mixpanel";
import { useCurrentUser } from "./domains/user/hooks/useCurrentUser";
import { useClaimAnonymousConversations } from "./domains/gpt/anonymous/anonymousGpt";
import { useApiQuery } from "./services/api";
import { extractRoomIdFromPath } from "./domains/room/utils/roomIdFromPath";
import type { ShellRoomState } from "./domains/app/shellRoom";
import { matchRoute } from "./components/routing/RouteMatch";
import VisualAbstractPage, {
  VisualAbstractNotFound,
} from "./features/visual-abstract/VisualAbstractPage";
import { useVisualAbstractWatcher } from "./features/visual-abstract/useVisualAbstractWatcher";

async function initialize() {
  // Load features asynchronously when the app renders
  initGrowthbook().then(
    () => {
      console.log("growthbook initialized", growthbook.getFeatures());
    },
    (error: unknown) => {
      // Fail closed and unblock routes waiting for the initial flag payload.
      growthbook.setFeatures({});
      console.warn("growthbook init failed", error);
    }
  );

  if (isIOSApp()) {
    iosSetBackgroundColor("#FFFFFF");
    iosGetSafeArea(); // Getting iOS Device safeAreaTop
  }

  // Google Analytics
  if (globalThis.location.host.includes("juisci.com")) {
    gaInitializeTagManager();
    gaSetCampaignDetails();
    gaStartEngagementHeartbeat();
  }
}

/**
 * Router-level shell data. Reads URL once, fetches the room once, and feeds
 * `ShellRoomState` as an explicit prop to shell components that render above
 * any specific Route (`LangSync`, `AudioProvider` → `MiniAudioPlayer`,
 * `AppLayout` → `LanguagePanel`). Also performs the mixpanel user sync and
 * storage cleanup that used to live in `RouterHooks`.
 *
 * Only this component and `useRouterPageView` are allowed to peek the URL
 * for room information — every other consumer receives props.
 */
function AppShell({
  children,
}: {
  children: (shellRoom: ShellRoomState) => React.ReactNode;
}) {
  useMixpanel(env.mixpanelToken);
  // After useMixpanel: a cold start queued before this mount reaches Mixpanel.
  useAppForegroundedTracking();
  useStorageCleanup();
  // Deliberately inside the shell, never above the `/v` branch: that route is a
  // capability URL served by a minimal shell on purpose
  // (`.knowledge/mixpanel-capability-url-leak.md`), and a reader who opened a
  // shared poster owns no generation to be told about.
  useVisualAbstractWatcher();
  const { currentUser, isAuthenticated, isLoading } = useCurrentUser();
  const roomsQuery = useApiQuery(
    "/webapp/private/rooms",
    currentUser ? { durable: true } : false
  );
  const roomNames =
    roomsQuery.state === "success"
      ? roomsQuery.body.rooms.map((r) => r.translations.en.name)
      : null;
  useMixpanelUser(currentUser, roomNames);
  const firstQuestionQuery = useApiQuery(
    "/webapp/private/gpt/first-question",
    currentUser === null ? false : {}
  );
  useMixpanelJuisciGptOnboarded(
    currentUser === null
      ? isAuthenticated || isLoading
        ? { kind: "unknown" }
        : { kind: "signed_out" }
      : firstQuestionQuery.state === "success"
        ? {
            kind: "signed_in",
            firstQuestionAt: firstQuestionQuery.body.firstQuestionAt,
          }
        : { kind: "unknown" }
  );
  // The conversations this browser had while logged out become this
  // account's, and the browser is marked as a member's.
  useClaimAnonymousConversations(currentUser === null ? null : currentUser.id);

  // Sync stable identity attributes for deterministic rollout targeting.
  const currentEmail = currentUser === null ? null : currentUser.email;
  const currentUid = currentUser === null ? null : currentUser.uid;
  // The declared Specialty rides the SAME call, because it is what a rollout
  // targets: the practice feature is enabled specialty by specialty from the
  // GrowthBook console, one rule at 100% of its target. Published here rather
  // than in a second effect so the two never overwrite each other's spread —
  // and re-published when it changes, since somebody who re-declares their
  // specialty has moved between rules.
  const currentSpecialty =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;
  // The pinned room rides the SAME call for the same reason: a client whose
  // members land on JuisciGPT rather than in their room is enabled room by room
  // from the console (`GPT_HOME_FOR_ROOM_MEMBERS`), and the room is the only
  // term that rule can be written on. Email cannot: on the first client to ask,
  // 15 of 58 members carry the company domain and the rest are gmail and Apple
  // relay addresses.
  const currentPinnedRoom =
    currentUser === null ? null : currentUser.pinnedRoom;
  // The staff switch of the experimental features (settings). The GrowthBook
  // rule of `EXPERIMENTAL` serves true on `experimental_features`; a user who
  // is not staff gets null from the api and so false here.
  const currentExperimentalFeatures =
    currentUser === null ? false : currentUser.experimentalFeatures === true;
  useEffect(() => {
    // Cleared first: until THIS user's attributes have landed, GrowthBook is
    // still hashing on the previous identity (or the anonymous one), and a
    // per-user-stable cohort read must not resolve off that.
    growthbookIdentityStore.set(null);
    if (currentEmail === null || currentUid === null) {
      // Signed out: the staff switch of the previous session must not keep
      // `EXPERIMENTAL` on for whoever uses this tab next.
      void growthbook
        .setAttributes({
          ...growthbook.getAttributes(),
          experimental_features: false,
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
        });
      return;
    }

    // Superseded by a later identity change (an account switch without a
    // remount), this resolution must not publish the user it was started for.
    // Without the guard, a slow first call landing after a fast second one
    // writes the PREVIOUS uid, and every reader comparing the store to the
    // signed-in user then stays `pending` forever — a rail that never appears,
    // for a reason nothing in the UI could show.
    let superseded = false;

    void growthbook
      .setAttributes({
        ...growthbook.getAttributes(),
        email: currentEmail,
        user_id: currentUid,
        specialty: currentSpecialty,
        pinned_room: currentPinnedRoom,
        experimental_features: currentExperimentalFeatures,
      })
      .then(
        () => {
          if (superseded) return;
          growthbookIdentityStore.set({ uid: currentUid, status: "synced" });
        },
        (error: unknown) => {
          // The identity never landed, so GrowthBook is still bucketing on the
          // anonymous attributes. Per-user cohorts stay `pending` rather than
          // resolve on an assignment that would not be stable for this account —
          // a rail that does not appear beats one that flips arms between visits.
          Sentry.captureException(error);
          if (superseded) return;
          growthbookIdentityStore.set({ uid: currentUid, status: "failed" });
        }
      );

    return () => {
      superseded = true;
    };
  }, [
    currentEmail,
    currentUid,
    currentSpecialty,
    currentPinnedRoom,
    currentExperimentalFeatures,
  ]);

  const location = useLocation();
  const roomId = extractRoomIdFromPath(location.pathname);
  const shellRoomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const shellRoom: ShellRoomState =
    roomId === null
      ? { kind: "non-room" }
      : shellRoomQuery.state === "success"
        ? { kind: "loaded", room: shellRoomQuery.body }
        : { kind: "loading" };

  return <>{children(shellRoom)}</>;
}

function AppRouter(): React.ReactElement {
  return (
    <Switch>
      <Route path="/v">
        <Switch>
          {matchRoute(
            "/v/:token",
            ({ token }) => (
              <VisualAbstractPage token={token} />
            ),
            { exact: true }
          )}
          <Route>
            <VisualAbstractNotFound />
          </Route>
        </Switch>
      </Route>
      <Route>
        <AppShell>
          {(shellRoom) => (
            <LangSync shellRoom={shellRoom}>
              <AudioProvider>
                <Redirector />
                {/* Suspense is required for lazy loading of routes */}
                <Suspense fallback={<Loader expanded />}>
                  <AppLayout>
                    <AppRoutes />
                  </AppLayout>
                </Suspense>
                {/* The app-wide sheets sit outside AppLayout, so they get their
          own scroll-to-dismiss; `contents` keeps the wrapper out of the layout. */}
                <div
                  className="contents"
                  onTouchMove={dismissNativeKeyboardOnDrag}
                >
                  <SettingsSheet />
                  {/* The welcome sheet of a join (docs/partner-objection.md),
          opened imperatively by every join entry, wherever the reader is. */}
                  <RoomWelcomeHost />
                  {/* The professional record a room asks for at its door, opened
          imperatively by every join entry (`askForProfessionalRecord`). */}
                  <ProfessionalRecordHost />
                  {/* Mounted once app-wide, like the settings sheet, and opened imperatively
          from a chat answer's action bar — the annotator never navigates to it. */}
                  <AnnotationSheet />
                </div>
              </AudioProvider>
            </LangSync>
          )}
        </AppShell>
      </Route>
    </Switch>
  );
}

export default function App() {
  useIsOffline();
  useRequestReview();
  useEffect(() => {
    initialize();
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <AppToaster placement={isDesktopShell() ? "top-right" : "top-center"} />
      <DefaultThemeProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: localStoragePersister,
            buster: QUERY_CACHE_VERSION,
            dehydrateOptions: {
              // `shouldDehydrateQuery` REPLACES the library default rather
              // than extending it, so it must re-assert it: the default is
              // `status === "success"`, and without it every pending and
              // errored query is persisted too. A dehydrated pending query
              // carries its in-flight promise, so navigating away mid-fetch
              // (cancelling it) rejects that promise and logs "A query that
              // was dehydrated as pending ended up rejecting". Only settled,
              // successful data belongs in localStorage.
              shouldDehydrateQuery: (query) =>
                defaultShouldDehydrateQuery(query) &&
                !NEVER_PERSISTED_ROUTES.has(query.queryKey[1]),
            },
          }}
        >
          <Router>
            <Sentry.ErrorBoundary
              fallback={({ error, resetError }) => (
                <ErrorFallback
                  error={
                    error instanceof Error ? error : new Error(String(error))
                  }
                  resetErrorBoundary={resetError}
                />
              )}
            >
              <AppRouter />
            </Sentry.ErrorBoundary>
          </Router>
        </PersistQueryClientProvider>
      </DefaultThemeProvider>
    </GrowthBookProvider>
  );
}
